<template>
    <Teleport to="body">
        <Transition name="confirm-fade">
            <div v-if="modelValue" class="confirm-scoring-overlay" @click.self="cancel">
                <div class="confirm-scoring-dialog">
                    <!-- Close button -->
                    <button class="confirm-scoring-close" @click="cancel">
                        <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
                            <path d="M1 1L13 13M13 1L1 13" stroke="#9CA3AF" stroke-width="1.5" stroke-linecap="round" />
                        </svg>
                    </button>

                    <!-- Warning icon -->
                    <div class="confirm-scoring-icon">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
                            <path
                                d="M12 9V13M12 17H12.01M10.29 3.86L1.82 18A2 2 0 003.54 21H20.46A2 2 0 0022.18 18L13.71 3.86A2 2 0 0010.29 3.86Z"
                                stroke="#D97706" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" />
                        </svg>
                    </div>

                    <!-- Title -->
                    <h3 class="confirm-scoring-title">{{ $t("Kpi.confirm_task_completion") }}</h3>

                    <!-- Message -->
                    <p class="confirm-scoring-message">
                        {{ $t("Kpi.confirm_scoring_prefix") }}
                        <strong>{{ $t("Kpi.locked") }}</strong>
                        {{ $t("Kpi.confirm_scoring_suffix") }}
                    </p>

                    <!-- Actions -->
                    <div class="confirm-scoring-actions">
                        <button class="confirm-scoring-btn confirm-scoring-btn-cancel" @click="cancel"
                            :disabled="loading">
                            {{ $t("Common.cancel") }}
                        </button>
                        <button class="confirm-scoring-btn confirm-scoring-btn-confirm" @click="confirm"
                            :disabled="loading">
                            <span v-if="loading" class="confirm-scoring-spinner"></span>
                            <span v-else>{{ $t("Kpi.confirm_lock_task") }}</span>
                        </button>
                    </div>
                </div>
            </div>
        </Transition>
    </Teleport>
</template>

<script setup>
import { defineProps, defineEmits } from 'vue';

const props = defineProps({
    modelValue: {
        type: Boolean,
        default: false,
    },
    loading: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits(['update:modelValue', 'confirm', 'cancel']);

function cancel() {
    if (props.loading) return;
    emit('update:modelValue', false);
    emit('cancel');
}

function confirm() {
    emit('confirm');
}
</script>

<style scoped>
.confirm-scoring-overlay {
    position: fixed;
    inset: 0;
    z-index: 9999;
    display: flex;
    align-items: center;
    justify-content: center;
    background-color: rgba(0, 0, 0, 0.4);
    backdrop-filter: blur(2px);
}

.confirm-scoring-dialog {
    position: relative;
    background: #fff;
    border-radius: 12px;
    padding: 28px 24px 24px;
    width: 400px;
    max-width: calc(100vw - 32px);
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.15);
    text-align: center;
}

.confirm-scoring-close {
    position: absolute;
    top: 16px;
    right: 16px;
    background: none;
    border: none;
    cursor: pointer;
    padding: 4px;
    border-radius: 4px;
    display: flex;
    align-items: center;
    justify-content: center;
}

.confirm-scoring-close:hover {
    background-color: #F3F4F6;
}

.confirm-scoring-icon {
    width: 48px;
    height: 48px;
    border-radius: 50%;
    background-color: #FEF3C7;
    display: flex;
    align-items: center;
    justify-content: center;
    margin: 0 auto 16px;
}

.confirm-scoring-title {
    font-size: 18px;
    font-weight: 600;
    color: #111827;
    margin: 0 0 10px;
    line-height: 1.3;
}

.confirm-scoring-message {
    font-size: 14px;
    color: #6B7280;
    line-height: 1.55;
    margin: 0 0 24px;
    padding: 0 8px;
}

.confirm-scoring-message strong {
    color: #374151;
    font-weight: 600;
}

.confirm-scoring-actions {
    display: flex;
    align-items: center;
    justify-content: center;
    gap: 12px;
}

.confirm-scoring-btn {
    padding: 9px 20px;
    border-radius: 8px;
    font-size: 14px;
    font-weight: 500;
    cursor: pointer;
    border: none;
    transition: all 0.15s ease;
    min-width: 100px;
}

.confirm-scoring-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.confirm-scoring-btn-cancel {
    background-color: #fff;
    color: #374151;
    border: 1px solid #D1D5DB;
}

.confirm-scoring-btn-cancel:hover:not(:disabled) {
    background-color: #F9FAFB;
    border-color: #9CA3AF;
}

.confirm-scoring-btn-confirm {
    background-color: #2563EB;
    color: #fff;
    min-width: 160px;
}

.confirm-scoring-btn-confirm:hover:not(:disabled) {
    background-color: #1D4ED8;
}

.confirm-scoring-btn-confirm:active:not(:disabled) {
    background-color: #1E40AF;
}

/* Spinner */
.confirm-scoring-spinner {
    display: inline-block;
    width: 16px;
    height: 16px;
    border: 2px solid rgba(255, 255, 255, 0.3);
    border-top-color: #fff;
    border-radius: 50%;
    animation: confirm-spin 0.6s linear infinite;
}

@keyframes confirm-spin {
    to {
        transform: rotate(360deg);
    }
}

/* Transition */
.confirm-fade-enter-active,
.confirm-fade-leave-active {
    transition: opacity 0.2s ease;
}

.confirm-fade-enter-active .confirm-scoring-dialog,
.confirm-fade-leave-active .confirm-scoring-dialog {
    transition: transform 0.2s ease, opacity 0.2s ease;
}

.confirm-fade-enter-from,
.confirm-fade-leave-to {
    opacity: 0;
}

.confirm-fade-enter-from .confirm-scoring-dialog {
    transform: scale(0.95) translateY(10px);
}

.confirm-fade-leave-to .confirm-scoring-dialog {
    transform: scale(0.95) translateY(10px);
}
</style>
